"use client";
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { gsap } from "gsap";
import "./route-transition.css";

type Phase = "idle" | "covering" | "navigating" | "revealing";
const RouteContext = createContext<{
  phase: Phase;
  navigate: (href: string, x: number, y: number) => void;
}>({ phase: "idle", navigate: () => undefined });
export const useRouteTransition = () => useContext(RouteContext);

export default function RouteTransitionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const overlay = useRef<HTMLDivElement>(null);
  const animation = useRef<gsap.core.Timeline>();
  const destination = useRef<string>();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const previousOverflow = useRef("");
  const busy = useRef(false);
  const [phase, setPhase] = useState<Phase>("idle");

  const uncover = () => {
    if (!overlay.current || !busy.current) return;
    clearTimeout(timer.current);
    destination.current = undefined;
    setPhase("revealing");
    // The circle has already covered the viewport. Blend directly into the
    // committed page without introducing another wipe or moving the page.
    gsap.set(overlay.current, { clipPath: "none" });
    animation.current = gsap
      .timeline({
        onComplete: () => {
          if (overlay.current) overlay.current.style.display = "none";
          busy.current = false;
          document.documentElement.style.overflow = previousOverflow.current;
          setPhase("idle");
        },
      })
      .to(overlay.current, { opacity: 0, duration: 0.24, ease: "power1.out" });
  };

  useLayoutEffect(() => {
    if (destination.current === pathname) uncover();
  }, [pathname]);
  useLayoutEffect(
    () => () => {
      animation.current?.kill();
      clearTimeout(timer.current);
      if (busy.current)
        document.documentElement.style.overflow = previousOverflow.current;
    },
    []
  );

  const navigate = (href: string, x: number, y: number) => {
    if (busy.current || !overlay.current) return;
    const target = new URL(href, window.location.href);
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      target.pathname === pathname
    ) {
      router.push(href);
      return;
    }
    busy.current = true;
    destination.current = target.pathname;
    previousOverflow.current = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    setPhase("covering");
    const radius =
      Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y)) + 2;
    gsap.set(overlay.current, {
      display: "block",
      opacity: 1,
      clipPath: `circle(0px at ${x}px ${y}px)`,
    });
    animation.current = gsap
      .timeline({
        onComplete: () => {
          setPhase("navigating");
          router.push(href);
          // Release the cover if a navigation is cancelled or fails to commit.
          timer.current = setTimeout(uncover, 6000);
        },
      })
      .to(overlay.current, {
        clipPath: `circle(${radius}px at ${x}px ${y}px)`,
        duration: 0.6,
        ease: "power3.inOut",
      });
  };

  return (
    <RouteContext.Provider value={{ phase, navigate }}>
      {children}
      <div
        ref={overlay}
        className="route-bubble"
        data-phase={phase}
        aria-hidden="true"
      />
    </RouteContext.Provider>
  );
}

export function TransitionLink({
  href,
  onClick,
  children,
  ...props
}: Omit<React.ComponentProps<typeof Link>, "href"> & { href: string }) {
  const transition = useRouteTransition();
  return (
    <Link
      {...props}
      href={href}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          props.target === "_blank"
        )
          return;
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const keyboard = event.detail === 0;
        transition.navigate(
          href,
          keyboard ? rect.left + rect.width / 2 : event.clientX,
          keyboard ? rect.top + rect.height / 2 : event.clientY
        );
      }}
    >
      {children}
    </Link>
  );
}
